import { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { NAV } from '@/nav';
import { useAuth } from '@/state/auth';
import { useAlarms } from '@/lib/query';
import { useEventStream } from '@/lib/useEventStream';
import { Icon } from './Icons';
import { Sidebar } from './Sidebar';
import { ChangePasswordModal } from '@/components/ChangePasswordModal';

const MOBILE_TABS = [
  { to: '/', label: '驾驶舱', icon: 'dashboard' },
  { to: '/hang', label: '吊养', icon: 'hang' },
  { to: '/alarms', label: '报警', icon: 'alarm' },
  { to: '/lab', label: '化验', icon: 'lab' },
];

function UserChip() {
  const { user, logout } = useAuth();
  const [menu, setMenu] = useState(false);
  const [showChange, setShowChange] = useState(false);
  const nav = useNavigate();
  return (
    <div className="relative">
      <button
        onClick={() => setMenu((m) => !m)}
        aria-haspopup="menu"
        aria-expanded={menu}
        className="flex items-center gap-2 rounded-lg border border-hairline px-2.5 py-1.5 text-[13px] text-slate-600 hover:bg-slate-50"
      >
        <span className="grid h-5 w-5 place-items-center rounded-full bg-brand-100 text-[11px] font-bold text-brand-700">
          {(user?.displayName ?? '访客').slice(0, 1)}
        </span>
        {user?.displayName ?? '未登录'}
      </button>
      {menu ? (
        <div role="menu" className="absolute right-0 z-20 mt-1 w-44 rounded-lg border border-hairline bg-panel py-1 shadow-pop">
          <div className="px-3 py-1.5 text-[12px] text-slate-400">角色：{user?.role ?? '—'}</div>
          {user?.role === 'admin' ? (
            <button role="menuitem" onClick={() => { setMenu(false); nav('/admin/users'); }} className="block w-full px-3 py-1.5 text-left text-[13px] text-slate-600 hover:bg-slate-50">
              用户管理
            </button>
          ) : null}
          <button role="menuitem" onClick={() => { setMenu(false); setShowChange(true); }} className="block w-full px-3 py-1.5 text-left text-[13px] text-slate-600 hover:bg-slate-50">
            修改密码
          </button>
          <button role="menuitem" onClick={() => { logout(); setMenu(false); }} className="block w-full px-3 py-1.5 text-left text-[13px] text-slate-600 hover:bg-slate-50">
            退出登录
          </button>
        </div>
      ) : null}
      {showChange ? <ChangePasswordModal onClose={() => setShowChange(false)} /> : null}
    </div>
  );
}

function BellButton() {
  const { data: alarms } = useAlarms();
  const nav = useNavigate();
  const activeCount = alarms?.active.length ?? 0;
  return (
    <button
      onClick={() => nav('/alarms')}
      className="relative grid h-9 w-9 place-items-center rounded-lg text-slate-500 hover:bg-slate-100"
      aria-label={`通知，${activeCount} 条活动告警，查看报警中心`}
    >
      <Icon name="alarm" size={19} />
      {activeCount > 0 ? <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-alarm-fg" aria-hidden="true" /> : null}
    </button>
  );
}

function SettingsButton() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [showChange, setShowChange] = useState(false);
  const nav = useNavigate();
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="hidden h-9 w-9 place-items-center rounded-lg text-slate-500 hover:bg-slate-100 sm:grid"
        aria-label="设置"
      >
        <Icon name="settings" size={19} />
      </button>
      {open ? (
        <div role="menu" className="absolute right-0 z-20 mt-1 w-48 rounded-lg border border-hairline bg-panel py-1 shadow-pop">
          <div className="px-3 py-1.5 text-[12px] text-slate-400">当前角色：{user?.role ?? '未登录'}</div>
          {user?.role === 'admin' ? (
            <button role="menuitem" onClick={() => { setOpen(false); nav('/admin/users'); }} className="block w-full px-3 py-1.5 text-left text-[13px] text-slate-600 hover:bg-slate-50">
              用户管理
            </button>
          ) : null}
          <button role="menuitem" onClick={() => { setOpen(false); nav('/alarms'); }} className="block w-full px-3 py-1.5 text-left text-[13px] text-slate-600 hover:bg-slate-50">
            报警中心
          </button>
          <button role="menuitem" onClick={() => { setOpen(false); setShowChange(true); }} className="block w-full px-3 py-1.5 text-left text-[13px] text-slate-600 hover:bg-slate-50">
            修改密码
          </button>
          <button role="menuitem" onClick={() => { logout(); setOpen(false); }} className="block w-full px-3 py-1.5 text-left text-[13px] text-slate-600 hover:bg-slate-50">
            退出登录
          </button>
          <div className="mt-1 border-t border-hairline px-3 py-1.5 text-[11px] text-slate-400">RAS 平台 v1.0.0</div>
        </div>
      ) : null}
      {showChange ? <ChangePasswordModal onClose={() => setShowChange(false)} /> : null}
    </div>
  );
}

function TopBar({ onMenu }: { onMenu: () => void }) {
  const loc = useLocation();
  const current = NAV.find((n) => (n.to === '/' ? loc.pathname === '/' : loc.pathname.startsWith(n.to)));
  return (
    <header className="flex h-14 shrink-0 items-center justify-between border-b border-hairline bg-panel px-4 sm:px-6">
      <div className="flex items-center gap-2">
        <button onClick={onMenu} className="grid h-9 w-9 place-items-center rounded-lg text-slate-500 hover:bg-slate-100 lg:hidden" aria-label="打开导航菜单" aria-haspopup="dialog">
          <Icon name="menu" size={20} />
        </button>
        <span className="text-[13px] font-medium text-slate-400">{current?.label ?? ''}</span>
      </div>
      <div className="flex items-center gap-1.5">
        <BellButton />
        <SettingsButton />
        <div className="hidden sm:block">
          <UserChip />
        </div>
      </div>
    </header>
  );
}

function MobileTabBar({ onMore }: { onMore: () => void }) {
  const loc = useLocation();
  const nav = useNavigate();
  return (
    <nav className="safe-bottom flex shrink-0 items-stretch border-t border-hairline bg-panel lg:hidden" aria-label="快捷导航">
      {MOBILE_TABS.map((t) => {
        const active = t.to === '/' ? loc.pathname === '/' : loc.pathname.startsWith(t.to);
        return (
          <button key={t.to} onClick={() => nav(t.to)} aria-current={active ? 'page' : undefined} className={active ? 'flex flex-1 flex-col items-center gap-0.5 py-2 text-brand-600' : 'flex flex-1 flex-col items-center gap-0.5 py-2 text-slate-500'}>
            <Icon name={t.icon} size={20} />
            <span className="text-[11px]">{t.label}</span>
          </button>
        );
      })}
      <button onClick={onMore} className="flex flex-1 flex-col items-center gap-0.5 py-2 text-slate-500" aria-label="更多模块">
        <Icon name="grid" size={20} />
        <span className="text-[11px]">更多</span>
      </button>
    </nav>
  );
}

export function Shell() {
  useEventStream();
  const [navOpen, setNavOpen] = useState(false);
  return (
    <div className="flex h-screen w-full overflow-hidden">
      <a href="#main" className="skip-link">跳转到主内容</a>
      <Sidebar open={navOpen} onClose={() => setNavOpen(false)} />
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar onMenu={() => setNavOpen(true)} />
        <main id="main" className="flex-1 overflow-y-auto px-4 py-5 sm:px-8 sm:py-6">
          <Outlet />
        </main>
        <MobileTabBar onMore={() => setNavOpen(true)} />
      </div>
    </div>
  );
}
